import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Plus, X, Save, CheckCircle, History, Truck, Search, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatISTDateTime } from '../utils/timezone';
import { useDebounce } from '../hooks/useDebounce';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const OrderForm = () => {
  const { orderNumber } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isNew = !orderNumber;

  const [order, setOrder] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const debouncedCustomerSearch = useDebounce(customerSearch, 300);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [selectedCustomerObj, setSelectedCustomerObj] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const debouncedProductSearch = useDebounce(productSearch, 300);
  const [productLoading, setProductLoading] = useState(false);
  const [specialPrices, setSpecialPrices] = useState({}); // Map of design_number -> special_price
  const [applyGst, setApplyGst] = useState(false);
  const GST_PERCENTAGE = 5;

  useEffect(() => {
    if (!isNew) {
      fetchOrder();
    }
    
    if (isNew && user?.role === 'customer' && !selectedCustomer) {
      const createCustomerOrder = async () => {
        setLoading(true);
        try {
          const response = await axios.post(`${API}/orders`, {
            customer_email: user.email
          });
          toast.success('Order created successfully');
          navigate(`/orders/${response.data.order_number}`);
        } catch (error) {
          toast.error('Failed to create order');
          navigate('/orders');
        } finally {
          setLoading(false);
        }
      };
      createCustomerOrder();
    }
  }, [orderNumber, isNew, user?.role, user?.email]);

  const fetchCustomers = useCallback(async (search = '') => {
    setCustomerLoading(true);
    try {
      const params = new URLSearchParams({ limit: '30' });
      if (search && search.trim()) params.set('search', search.trim());
      const response = await axios.get(`${API}/users/customers?${params}`);
      const customersData = Array.isArray(response.data) ? response.data : [];
      setCustomers(customersData);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to fetch customers');
      setCustomers([]);
    } finally {
      setCustomerLoading(false);
    }
  }, []);

  // Debounced server-side customer search (new order, admin/sales only)
  useEffect(() => {
    if (isNew && (user?.role === 'admin' || user?.role === 'sales')) {
      fetchCustomers(debouncedCustomerSearch);
    }
  }, [debouncedCustomerSearch, isNew, user?.role, fetchCustomers]);

  const fetchProducts = useCallback(async (search = '') => {
    setProductLoading(true);
    try {
      const params = new URLSearchParams({ page: '1', per_page: '30' });
      if (search && search.trim()) params.set('search', search.trim());
      const response = await axios.get(`${API}/products?${params}`);
      const data = response.data;
      const list = Array.isArray(data.products) ? data.products : (Array.isArray(data) ? data : []);
      setProducts(list);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setProducts([]);
    } finally {
      setProductLoading(false);
    }
  }, []);

  // Debounced server-side product search
  useEffect(() => {
    fetchProducts(debouncedProductSearch);
  }, [debouncedProductSearch, fetchProducts]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderNumber}`);
      const orderData = response.data;
      if (orderData && orderData.order_number) {
        setOrder(orderData);
        setSelectedItems(Array.isArray(orderData.items) ? orderData.items : []);
        setSelectedCustomer(orderData.customer_email || '');
        // Load GST state from order
        setApplyGst(orderData.gst_applied || false);
        // Fetch this order's customer directly (for price tier) so we don't rely
        // on a preloaded customer list.
        if (orderData.customer_email && (user?.role === 'admin' || user?.role === 'sales')) {
          try {
            const cr = await axios.get(`${API}/users/customers?search=${encodeURIComponent(orderData.customer_email)}&limit=5`);
            const match = (Array.isArray(cr.data) ? cr.data : []).find(c => c.email === orderData.customer_email);
            if (match) setSelectedCustomerObj(match);
          } catch (e) { /* non-fatal: price tier falls back to bulk */ }
        }
      } else {
        toast.error('Invalid order data received');
        navigate('/orders');
      }
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Failed to fetch order');
      navigate('/orders');
    }
  };

  const fetchOrderHistory = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderNumber}/history`);
      // Ensure history is always an array
      const historyData = Array.isArray(response.data) ? response.data : [];
      setOrderHistory(historyData);
      setShowHistory(true);
    } catch (error) {
      console.error('Failed to fetch order history:', error);
      toast.error('Failed to fetch order history');
      setOrderHistory([]);
    }
  };

  // Fetch special prices for the customer
  const fetchSpecialPrices = useCallback(async (customerEmail) => {
    if (!customerEmail) {
      setSpecialPrices({});
      return;
    }
    try {
      const response = await axios.get(`${API}/special-pricing/customer/${encodeURIComponent(customerEmail)}`);
      const pricesMap = {};
      (response.data.special_prices || []).forEach(sp => {
        pricesMap[sp.design_number] = sp.special_price;
      });
      setSpecialPrices(pricesMap);
    } catch (error) {
      console.error('Failed to fetch special prices:', error);
      setSpecialPrices({});
    }
  }, []);

  // Fetch special prices when customer changes
  useEffect(() => {
    const customerEmail = order?.customer_email || selectedCustomer;
    if (customerEmail) {
      fetchSpecialPrices(customerEmail);
    }
  }, [order?.customer_email, selectedCustomer, fetchSpecialPrices]);

  const getCustomerPriceTier = () => {
    // Prefer the explicitly selected/loaded customer object (works regardless of
    // how many customers exist, since search only returns a subset).
    if (selectedCustomerObj?.price_tier) {
      return selectedCustomerObj.price_tier;
    }

    if (isNew && selectedCustomer) {
      const customer = customers.find(c => c.email === selectedCustomer);
      return customer?.price_tier || 'bulk';
    }
    
    if (order && order.customer_email) {
      const customer = customers.find(c => c.email === order.customer_email);
      if (customer?.price_tier) {
        return customer.price_tier;
      }
      return 'bulk';
    }
    
    return user?.price_tier || 'bulk';
  };

  const addProduct = (product) => {
    const priceTier = getCustomerPriceTier();
    // Check for special price first, fallback to tier price
    const hasSpecialPrice = specialPrices.hasOwnProperty(product.design_number);
    const unitPrice = hasSpecialPrice 
      ? specialPrices[product.design_number] 
      : product.pricing[priceTier];
    
    const sizes = [...new Set(product.variations.map(v => v.size))];
    const sizeQuantities = {};
    sizes.forEach(size => { sizeQuantities[size] = 0; });

    const newItem = {
      design_number: product.design_number,
      product_name: product.product_name,
      primary_image: product.primary_image,
      size_quantities: sizeQuantities,
      unit_price: unitPrice,
      subtotal: 0,
      on_hand: false,
      has_special_price: hasSpecialPrice // Track if this item uses special pricing
    };

    setSelectedItems([...selectedItems, newItem]);
  };

  const toggleOnHand = (itemIndex) => {
    const newItems = [...selectedItems];
    newItems[itemIndex].on_hand = !newItems[itemIndex].on_hand;
    setSelectedItems(newItems);
  };

  const updateQuantity = (itemIndex, size, quantity) => {
    const newItems = [...selectedItems];
    newItems[itemIndex].size_quantities[size] = parseInt(quantity) || 0;
    
    const totalQuantity = Object.values(newItems[itemIndex].size_quantities).reduce((sum, qty) => sum + qty, 0);
    newItems[itemIndex].subtotal = totalQuantity * newItems[itemIndex].unit_price;
    
    setSelectedItems(newItems);
  };

  const removeItem = (itemIndex) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== itemIndex));
  };

  const calculateSubtotal = () => {
    return selectedItems.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const calculateGstAmount = () => {
    if (!applyGst) return 0;
    return (calculateSubtotal() * GST_PERCENTAGE) / 100;
  };

  const calculateGrandTotal = () => {
    const subtotal = calculateSubtotal();
    const gstAmount = applyGst ? (subtotal * GST_PERCENTAGE) / 100 : 0;
    return subtotal + gstAmount;
  };

  const handleCreateOrder = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/orders`, {
        customer_email: selectedCustomer
      });
      toast.success('Order created successfully');
      navigate(`/orders/${response.data.order_number}`);
    } catch (error) {
      toast.error('Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const [stockShortageInfo, setStockShortageInfo] = useState(null); // {shortages, can_override}

  const submitOrderItems = async (forceOverride = false) => {
    const url = forceOverride
      ? `${API}/orders/${orderNumber}?force_override=true`
      : `${API}/orders/${orderNumber}`;
    await axios.put(url, {
      items: selectedItems,
      grand_total: calculateGrandTotal(),
      gst_applied: applyGst,
      gst_percentage: GST_PERCENTAGE,
      gst_amount: calculateGstAmount()
    });
  };

  const handleSaveOrder = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please add at least one product');
      return;
    }

    setLoading(true);
    try {
      await submitOrderItems(false);
      toast.success('Order saved successfully!');
      fetchOrder();
    } catch (error) {
      const data = error.response?.data;
      // Insufficient warehouse stock
      if (
        error.response?.status === 409 &&
        data?.detail?.error === 'insufficient_stock'
      ) {
        setStockShortageInfo({
          shortages: data.detail.shortages || [],
          can_override: !!data.detail.can_override,
        });
      } else {
        toast.error(data?.detail || 'Failed to save order');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOverrideSave = async () => {
    setLoading(true);
    try {
      await submitOrderItems(true);
      toast.success('Order saved with admin override');
      setStockShortageInfo(null);
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Override failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizeOrder = async () => {
    if (!window.confirm('Are you sure you want to finalize this order?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/orders/${orderNumber}/finalize`);
      toast.success('Order finalized successfully');
      fetchOrder();
    } catch (error) {
      toast.error('Failed to finalize order');
    } finally {
      setLoading(false);
    }
  };

  const handleDispatchOrder = async () => {
    if (!window.confirm('Are you sure you want to mark this order as dispatched?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/orders/${orderNumber}/dispatch`);
      toast.success('Order dispatched successfully');
      fetchOrder();
    } catch (error) {
      toast.error('Failed to dispatch order');
    } finally {
      setLoading(false);
    }
  };

  const canEdit = () => {
    if (!order) return true;
    if (order.status === 'cancelled') return false;
    if (order.status === 'draft') return true;
    if ((order.status === 'finalized' || order.status === 'dispatched') && (user?.role === 'admin' || user?.role === 'sales')) return true;
    return false;
  };

  // Server-side debounced search returns already-filtered products.
  // Keep a stable alias for downstream code that referenced `filteredProducts`.
  const filteredProducts = useMemo(() => Array.isArray(products) ? products : [], [products]);

  // Customer Selection Screen for Admin/Sales
  if (isNew && (user?.role === 'admin' || user?.role === 'sales')) {
    return (
      <Layout>
        <div className="max-w-lg mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="create-order-title">
              Create New Order
            </h1>
            <p className="mt-1 text-sm sm:text-base text-slate-600">
              Select a customer to begin
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">Select Customer</label>

            {/* Search input (server-side, debounced) */}
            <div className="relative mb-3">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Search by name, company, email or mobile..."
                data-testid="customer-search-input"
                className="w-full pl-9 pr-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
              />
            </div>

            {/* Selected customer summary */}
            {selectedCustomer && selectedCustomerObj && (
              <div
                className="flex items-center justify-between gap-2 mb-3 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-md"
                data-testid="customer-selected"
              >
                <span className="text-sm text-indigo-900 truncate">
                  <span className="font-semibold">{selectedCustomerObj.name}</span>{' '}
                  <span className="text-indigo-700">({selectedCustomerObj.email})</span>{' '}
                  <span className="text-xs uppercase text-indigo-600">· {selectedCustomerObj.price_tier}</span>
                </span>
                <button
                  type="button"
                  onClick={() => { setSelectedCustomer(''); setSelectedCustomerObj(null); }}
                  data-testid="customer-clear"
                  className="text-indigo-500 hover:text-indigo-700 shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Results list */}
            <div className="border border-slate-200 rounded-md max-h-72 overflow-y-auto mb-4" data-testid="customer-results">
              {customerLoading ? (
                <div className="px-4 py-6 text-center text-sm text-slate-400">Searching…</div>
              ) : customers.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-slate-400">
                  {customerSearch.trim() ? 'No matching customers' : 'No customers found'}
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {customers.map((customer) => {
                    const isSel = selectedCustomer === customer.email;
                    return (
                      <li key={customer.email}>
                        <button
                          type="button"
                          onClick={() => { setSelectedCustomer(customer.email); setSelectedCustomerObj(customer); }}
                          data-testid={`customer-option-${customer.email}`}
                          className={`w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors ${isSel ? 'bg-indigo-50' : ''}`}
                        >
                          <div className="text-sm font-medium text-slate-800">{customer.name || 'Unknown'}</div>
                          <div className="text-xs text-slate-500">
                            {customer.email} · <span className="uppercase">{customer.price_tier || 'N/A'}</span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <button
              onClick={handleCreateOrder}
              disabled={!selectedCustomer || loading}
              data-testid="confirm-customer-button"
              className="w-full px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium transition-all active:scale-[0.98] disabled:opacity-50 text-base"
            >
              {loading ? 'Creating...' : 'Continue'}
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // Loading state for customer creating order
  if (isNew && user?.role === 'customer' && loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Creating your order...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="order-form-title">
              {order ? `Order ${order.order_number}` : 'New Order'}
            </h1>
            {order && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <p className="text-sm sm:text-base text-slate-600">{order.customer_name}</p>
                {order.status === 'finalized' && (
                  <span className="px-2 sm:px-3 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">Finalized</span>
                )}
                {order.status === 'dispatched' && (
                  <span className="px-2 sm:px-3 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-600 border border-blue-200">Dispatched</span>
                )}
                {order.status === 'cancelled' && (
                  <span className="px-2 sm:px-3 py-1 text-xs font-medium rounded-full bg-rose-50 text-rose-600 border border-rose-200">Cancelled</span>
                )}
              </div>
            )}
          </div>
          {order && (user?.role === 'admin' || user?.role === 'sales') && (
            <button
              onClick={fetchOrderHistory}
              data-testid="view-history-button"
              className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 font-medium transition-colors text-sm"
            >
              <History className="w-4 h-4 mr-2" />
              History
            </button>
          )}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Product Catalog */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4">Product Catalog</h2>
              
              {/* Search Bar - Sticky */}
              <div className="sticky top-0 bg-white z-10 pb-3 mb-3 border-b border-slate-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search by design number, name, or color..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    data-testid="product-search-input"
                    className="w-full pl-9 pr-9 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                  {productSearch && (
                    <button
                      onClick={() => setProductSearch('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      aria-label="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {productSearch && (
                  <p className="text-xs text-slate-500 mt-2">
                    {productLoading ? 'Searching…' : `${filteredProducts.length} product${filteredProducts.length !== 1 ? 's' : ''} found`}
                  </p>
                )}
              </div>

              {/* Product Grid */}
              {filteredProducts.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">No products found</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Try searching by design number, product name, or color
                  </p>
                  {productSearch && (
                    <button
                      onClick={() => setProductSearch('')}
                      className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-h-[400px] sm:max-h-96 overflow-y-auto" data-testid="product-catalog">
                  {filteredProducts.map((product) => {
                    const hasSpecialPrice = specialPrices.hasOwnProperty(product.design_number);
                    const displayPrice = hasSpecialPrice 
                      ? specialPrices[product.design_number] 
                      : product.pricing[getCustomerPriceTier()];
                    const tierPrice = product.pricing[getCustomerPriceTier()];
                    
                    return (
                      <div
                        key={product.design_number}
                        className={`border rounded-lg p-3 sm:p-4 transition-colors ${hasSpecialPrice ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200 hover:border-indigo-300'}`}
                      >
                        <div className="flex gap-3">
                          <img src={product.primary_image} alt={product.product_name} className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded flex-shrink-0" loading="lazy" />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-slate-900 text-sm truncate">{product.product_name}</h3>
                            <p className="text-xs text-slate-500">#{product.design_number}</p>
                            <div className="mt-1 flex items-center gap-2">
                              {hasSpecialPrice ? (
                                <>
                                  <span className="text-sm font-bold text-amber-600">₹{displayPrice}</span>
                                  <span className="text-xs text-slate-400 line-through">₹{tierPrice}</span>
                                  <Star className="w-3.5 h-3.5 text-amber-500" title="Special Price" />
                                </>
                              ) : (
                                <span className="text-sm font-medium text-slate-900">₹{displayPrice}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => addProduct(product)}
                          disabled={!canEdit() || selectedItems.some(item => item.design_number === product.design_number)}
                          data-testid={`add-product-${product.design_number}`}
                          className="w-full mt-3 px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {selectedItems.some(item => item.design_number === product.design_number) ? 'Added' : 'Add to Order'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6 lg:sticky lg:top-20">
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4">Order Summary</h2>
              
              {selectedItems.length === 0 ? (
                <p className="text-slate-500 text-center py-6 sm:py-8 text-sm">No items added yet</p>
              ) : (
                <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6 max-h-[300px] sm:max-h-none overflow-y-auto" data-testid="order-summary">
                  {selectedItems.map((item, index) => (
                    <div 
                      key={index} 
                      className={`border rounded-lg p-3 sm:p-4 ${item.has_special_price ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200'}`}
                      data-testid={`order-item-${index}`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex gap-2 min-w-0">
                          <img src={item.primary_image} alt={item.product_name} className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1">
                              <h4 className="text-sm font-medium text-slate-900 truncate">{item.product_name}</h4>
                              {item.has_special_price && (
                                <Star className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" title="Special Price Applied" />
                              )}
                            </div>
                            <p className="text-xs text-slate-500">#{item.design_number}</p>
                            <p className={`text-xs font-medium ${item.has_special_price ? 'text-amber-600' : 'text-slate-600'}`}>
                              ₹{item.unit_price} each
                            </p>
                          </div>
                        </div>
                        {canEdit() && (
                          <button
                            onClick={() => removeItem(index)}
                            className="text-rose-600 hover:bg-rose-50 p-1 rounded transition-colors flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {Object.keys(item.size_quantities).map((size) => (
                          <div key={size} className="flex items-center gap-1">
                            <label className="text-xs text-slate-600 w-6">{size}:</label>
                            <input
                              type="number"
                              min="0"
                              value={item.size_quantities[size]}
                              onChange={(e) => updateQuantity(index, size, e.target.value)}
                              disabled={!canEdit()}
                              data-testid={`quantity-${index}-${size}`}
                              className="flex-1 w-full px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50"
                            />
                          </div>
                        ))}
                      </div>

                      {/* On Hand Quantity toggle - hidden for customers */}
                      {user?.role !== 'customer' && (
                        <div className="mt-3">
                          <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700 select-none">
                            <input
                              type="checkbox"
                              checked={!!item.on_hand}
                              onChange={() => toggleOnHand(index)}
                              disabled={!canEdit()}
                              data-testid={`on-hand-checkbox-${index}`}
                              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                            />
                            <span className="font-medium">On Hand Quantity</span>
                            <span className="text-slate-400">(skip warehouse deduction)</span>
                          </label>
                        </div>
                      )}

                      {/* Dispatch picklist (when picks exist) */}
                      {Array.isArray(item.picks) && item.picks.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <p className="text-xs font-semibold text-slate-700 mb-1">Dispatch From:</p>
                          <div className="flex flex-wrap gap-1.5">
                            {item.picks.map((p, pi) => (
                              <span
                                key={pi}
                                data-testid={`pick-${index}-${pi}`}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs rounded font-mono"
                              >
                                {p.rack_number}{p.size ? ` (${p.size})` : ''} → {p.quantity} pcs
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between text-sm">
                        <span className="text-slate-600">Subtotal:</span>
                        <span className={`font-medium ${item.has_special_price ? 'text-amber-600' : 'text-slate-900'}`}>
                          ₹{item.subtotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* GST Checkbox - before Total Amount section */}
              {selectedItems.length > 0 && canEdit() && (
                <div className="border-t border-slate-200 pt-4 mb-2">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={applyGst}
                      onChange={(e) => setApplyGst(e.target.checked)}
                      data-testid="gst-checkbox"
                      className="h-5 w-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                    <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
                      Apply GST (5%)
                    </span>
                  </label>
                </div>
              )}

              {/* Total Amount Section */}
              <div className="border-t border-slate-200 pt-4 mb-4">
                {/* Show subtotal and GST when GST is applied (editable mode) */}
                {applyGst && selectedItems.length > 0 && canEdit() && (
                  <>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-600">Subtotal:</span>
                      <span className="text-base font-medium text-slate-700">₹{calculateSubtotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm text-slate-600">GST (5%):</span>
                      <span className="text-base font-medium text-emerald-600" data-testid="gst-amount">₹{calculateGstAmount().toFixed(2)}</span>
                    </div>
                  </>
                )}
                {/* Show GST info for saved orders in read-only mode */}
                {order?.gst_applied && !canEdit() && (
                  <>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-600">Subtotal:</span>
                      <span className="text-base font-medium text-slate-700">₹{(order.grand_total - (order.gst_amount || 0)).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm text-slate-600">GST ({order.gst_percentage || 5}%):</span>
                      <span className="text-base font-medium text-emerald-600">₹{(order.gst_amount || 0).toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-base sm:text-lg font-semibold text-slate-900">Grand Total:</span>
                  <span className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="grand-total">₹{calculateGrandTotal().toFixed(2)}</span>
                </div>
              </div>

              {canEdit() && (
                <div className="space-y-2">
                  <button
                    onClick={handleSaveOrder}
                    disabled={loading || !orderNumber || selectedItems.length === 0}
                    data-testid="save-order-button"
                    className="w-full px-4 py-3 bg-slate-900 text-white rounded-md hover:bg-slate-800 font-medium transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center text-sm sm:text-base"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Saving...' : 'Save Order'}
                  </button>
                  
                  {selectedItems.length === 0 && (
                    <p className="text-xs text-center text-slate-500 py-2">
                      Add products to enable saving
                    </p>
                  )}
                  
                  {order && order.status === 'draft' && (user?.role === 'admin' || user?.role === 'sales') && (
                    <button
                      onClick={handleFinalizeOrder}
                      disabled={loading || selectedItems.length === 0}
                      data-testid="finalize-order-button"
                      className="w-full px-4 py-3 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 font-medium transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center text-sm sm:text-base"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Finalize Order
                    </button>
                  )}
                  
                  {order && order.status === 'finalized' && (user?.role === 'admin' || user?.role === 'sales') && (
                    <button
                      onClick={handleDispatchOrder}
                      disabled={loading}
                      data-testid="dispatch-order-button"
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center text-sm sm:text-base"
                    >
                      <Truck className="w-4 h-4 mr-2" />
                      Mark as Dispatched
                    </button>
                  )}
                </div>
              )}

              {order && order.status === 'cancelled' && (
                <div className="text-center text-sm text-rose-600 py-2 bg-rose-50 rounded border border-rose-200">
                  This order has been cancelled.
                </div>
              )}
              
              {order && (order.status === 'finalized' || order.status === 'dispatched') && user?.role === 'customer' && (
                <div className="text-center text-sm text-slate-600 py-2 bg-slate-50 rounded">
                  Order is {order.status}. Contact sales team for changes.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* History Modal */}
        {showHistory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowHistory(false)}>
            <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-2xl max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 sm:p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Order History</h2>
              </div>
              <div className="p-4 sm:p-6 overflow-y-auto max-h-[60vh]" data-testid="order-history-modal">
                <div className="space-y-4">
                  {orderHistory.map((entry, index) => (
                    <div key={index} className="border-l-4 border-indigo-600 pl-4 py-2">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 mb-1">
                        <span className="font-medium text-slate-900 capitalize">{entry.action.replace(/_/g, ' ')}</span>
                        <span className="text-xs text-slate-500">{formatISTDateTime(entry.timestamp)}</span>
                      </div>
                      <p className="text-sm text-slate-600">{entry.details}</p>
                      <p className="text-xs text-slate-500 mt-1">By: {entry.changed_by} ({entry.changed_by_role})</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-4 sm:p-6 border-t border-slate-200 sticky bottom-0 bg-white">
                <button
                  onClick={() => setShowHistory(false)}
                  data-testid="close-history-button"
                  className="w-full px-4 py-2.5 bg-slate-900 text-white rounded-md hover:bg-slate-800 font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Insufficient Stock Modal */}
        {stockShortageInfo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setStockShortageInfo(null)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="p-5 border-b border-slate-200 flex items-start gap-3">
                <div className="p-2 bg-rose-50 rounded-lg">
                  <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0l-7.1 12.25A2 2 0 005 19z" /></svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Insufficient Warehouse Stock</h3>
                  <p className="text-sm text-slate-500">The following items don't have enough stock:</p>
                </div>
              </div>
              <div className="p-5 max-h-72 overflow-y-auto">
                <ul className="space-y-2 text-sm">
                  {stockShortageInfo.shortages.map((s, i) => (
                    <li key={i} data-testid={`shortage-row-${i}`} className="flex items-center justify-between bg-rose-50/60 border border-rose-100 rounded px-3 py-2">
                      <div>
                        <p className="font-medium text-slate-900">{s.product_name || s.design_number}</p>
                        <p className="text-xs text-slate-500 font-mono">{s.design_number} • Size {s.size}</p>
                      </div>
                      <div className="text-right text-xs">
                        <p className="text-rose-700 font-semibold">Need {s.requested}</p>
                        <p className="text-slate-500">Have {s.available}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                {!stockShortageInfo.can_override && (
                  <p className="mt-3 text-xs text-slate-500">
                    Please reduce quantity, mark items as <span className="font-semibold">On Hand Quantity</span>, or ask an admin to override.
                  </p>
                )}
              </div>
              <div className="p-5 border-t border-slate-200 flex gap-2 justify-end">
                <button
                  onClick={() => setStockShortageInfo(null)}
                  data-testid="shortage-cancel-btn"
                  className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                {stockShortageInfo.can_override && (
                  <button
                    onClick={handleOverrideSave}
                    disabled={loading}
                    data-testid="shortage-override-btn"
                    className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-medium hover:bg-rose-700 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Override & Continue'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
